import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';
import type { ExamName } from '../examTasks/examTaskTypes';

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

export type AdminVocabularyBook = {
    id: string;
    bookId: string;
    examName: ExamName;
    title: string;
    description: string;
    coverKey: string;
    coverColor: string;
    displayOrder: number;
    isActive: boolean;
    wordCount: number;
    createdAt: string;
    updatedAt: string;
};

export type AdminVocabularyWord = {
    id: string;
    wordId: string;
    bookId: string;
    word: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
};

export type AdminPagedResult<T> = {
    page: number;
    pageSize: number;
    totalCount: number;
    items: T[];
};

export type AdminVocabularyBookInput = {
    examName: ExamName;
    title: string;
    description: string;
    coverKey: string;
    coverColor: string;
    displayOrder: number;
    isActive: boolean;
};

export type AdminVocabularyWordInput = {
    word: string;
    isActive: boolean;
};

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct = candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrap<T>(payload: T | AdminApiEnvelope<T>, fallback: string): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) return payload as T;
    const envelope = payload as AdminApiEnvelope<T>;
    if (envelope.success === false) throw new Error(getErrorMessage(envelope, fallback));
    return envelope.data !== undefined ? envelope.data : (payload as T);
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
}

function normalizeExamName(value: unknown): ExamName {
    return value === 'Core' ? 'Core' : 'Academic';
}

function normalizeBook(value: unknown): AdminVocabularyBook {
    const record = asRecord(value);
    const bookId = asString(record.bookId);
    return {
        id: bookId,
        bookId,
        examName: normalizeExamName(record.examName),
        title: asString(record.title),
        description: asString(record.description),
        coverKey: asString(record.coverKey),
        coverColor: asString(record.coverColor, '#22c7ba'),
        displayOrder: asNumber(record.displayOrder, 0),
        isActive: asBoolean(record.isActive, true),
        wordCount: asNumber(record.wordCount, 0),
        createdAt: asString(record.createdAt),
        updatedAt: asString(record.updatedAt),
    };
}

function normalizeWord(value: unknown): AdminVocabularyWord {
    const record = asRecord(value);
    const wordId = asString(record.wordId);
    return {
        id: wordId,
        wordId,
        bookId: asString(record.bookId),
        word: asString(record.word),
        isActive: asBoolean(record.isActive, true),
        createdAt: asString(record.createdAt),
        updatedAt: asString(record.updatedAt),
    };
}

function normalizePaged<T>(value: unknown, mapper: (value: unknown) => T): AdminPagedResult<T> {
    const payload = unwrap(value as AdminPagedResult<T> | AdminApiEnvelope<AdminPagedResult<T>>, 'Admin vocabulary request failed');
    const record = asRecord(payload);
    const items = Array.isArray(record.items) ? record.items.map(mapper) : [];
    return {
        page: asNumber(record.page, 1),
        pageSize: asNumber(record.pageSize, items.length || 1),
        totalCount: asNumber(record.totalCount, items.length),
        items,
    };
}

async function requestAdminVocabulary<T>(
    path: string,
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const requestPath = trimSlashes(path);
    const url = new URL(requestPath ? `${apiRoot}/AdminVocabulary/${requestPath}` : `${apiRoot}/AdminVocabulary`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') url.searchParams.set(key, value);
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) headers.set('Accept', 'application/json');
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    if (adminToken && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${adminToken}`);

    const response = await fetch(url.toString(), { ...init, headers });
    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();
    if (!response.ok) throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) throw new Error(getErrorMessage(envelope, 'Admin vocabulary request failed'));
    }
    return payload as T;
}

export async function getVocabularyBooks(params: {
    page: number;
    pageSize: number;
    search?: string;
    examName?: ExamName | '';
}): Promise<AdminPagedResult<AdminVocabularyBook>> {
    const response = await requestAdminVocabulary<unknown>('books', { method: 'GET' }, {
        page: String(params.page),
        pageSize: String(params.pageSize),
        search: params.search?.trim() ?? '',
        examName: params.examName ?? '',
    });
    return normalizePaged(response, normalizeBook);
}

export async function saveVocabularyBook(input: AdminVocabularyBookInput, bookId?: string): Promise<AdminVocabularyBook> {
    const response = await requestAdminVocabulary<unknown>(bookId ? `books/${bookId}` : 'books', {
        method: bookId ? 'PUT' : 'POST',
        body: JSON.stringify(input),
    });
    return normalizeBook(unwrap(response as AdminVocabularyBook | AdminApiEnvelope<AdminVocabularyBook>, 'Book save failed'));
}

export async function deleteVocabularyBook(bookId: string): Promise<void> {
    await requestAdminVocabulary(`books/${bookId}`, { method: 'DELETE' });
}

export async function getVocabularyWords(bookId: string, params: {
    page: number;
    pageSize: number;
    search?: string;
}): Promise<AdminPagedResult<AdminVocabularyWord>> {
    const response = await requestAdminVocabulary<unknown>(`books/${bookId}/words`, { method: 'GET' }, {
        page: String(params.page),
        pageSize: String(params.pageSize),
        search: params.search?.trim() ?? '',
    });
    return normalizePaged(response, normalizeWord);
}

export async function saveVocabularyWord(bookId: string, input: AdminVocabularyWordInput, wordId?: string): Promise<AdminVocabularyWord> {
    const response = await requestAdminVocabulary<unknown>(wordId ? `books/${bookId}/words/${wordId}` : `books/${bookId}/words`, {
        method: wordId ? 'PUT' : 'POST',
        body: JSON.stringify(input),
    });
    return normalizeWord(unwrap(response as AdminVocabularyWord | AdminApiEnvelope<AdminVocabularyWord>, 'Word save failed'));
}

export async function deleteVocabularyWord(bookId: string, wordId: string): Promise<void> {
    await requestAdminVocabulary(`books/${bookId}/words/${wordId}`, { method: 'DELETE' });
}
